import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRtv, useRtvAction, useRtvOptions } from "@/features/vendor-returns/api";
import { KindPill, RtvStatusBadge, VendorName } from "@/features/vendor-returns/bits";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/vendor-returns/$rtvId")({ component: RtvDetail });

function RtvDetail() {
  const { rtvId } = Route.useParams();
  const navigate = useNavigate();
  const { data: rtv, isLoading, isError, error } = useRtv(rtvId);
  const { data: opts } = useRtvOptions();
  const act = useRtvAction();
  const [remarks, setRemarks] = useState("");
  const [vendorRef, setVendorRef] = useState("");
  const [repQty, setRepQty] = useState<Record<string, string>>({});
  const [repRemarks, setRepRemarks] = useState<Record<string, string>>({});
  const [repRef, setRepRef] = useState("");

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (isError || !rtv) return <div className="text-sm text-red-600">{(error as Error)?.message ?? "Return not found."}</div>;
  const can = !!opts?.can_process;
  const returned = rtv.items.reduce((s, i) => s + Number(i.quantity), 0);

  const run = async (action: "dispatch" | "cancel" | "close" | "receive-replacement", body: Record<string, unknown>, ok: string) => {
    if (act.isPending) return;
    try { await act.mutateAsync({ id: rtv.id, action, body }); toast.success(ok); setRemarks(""); }
    catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/vendor-returns" })}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
      <Card>
        <CardHeader><CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2"><span className="font-mono">{rtv.rtv_number}</span><RtvStatusBadge status={rtv.status} /></span>
        </CardTitle></CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-4">
          <div><div className="text-xs text-muted-foreground">Vendor</div><VendorName name={rtv.vendor_name} oneTime={rtv.vendor_is_one_time} /></div>
          {rtv.origin === "department_return" && <div className="md:col-span-4 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">Raised from a department defective return ({rtv.department_name ?? "department"}). The GRN below only proves where the item was bought; stock leaves defective stock once, when this return is dispatched.</div>}
          <div><div className="text-xs text-muted-foreground">Source GRN</div><Link to="/grns/$grnId" params={{ grnId: rtv.grn }} className="text-primary hover:underline">{rtv.grn_number}</Link></div>
          <div><div className="text-xs text-muted-foreground">PO</div>{rtv.po ? <Link to="/purchase-orders/$poId" params={{ poId: rtv.po }} className="text-primary hover:underline">{rtv.po_number}</Link> : "—"}</div>
          <div><div className="text-xs text-muted-foreground">Vendor reference</div>{rtv.vendor_reference ?? "—"}</div>
          <div><div className="text-xs text-muted-foreground">Reason</div>{rtv.reason}</div>
          <div><div className="text-xs text-muted-foreground">Resolution</div>{rtv.resolution || "—"}</div>
          <div><div className="text-xs text-muted-foreground">Dispatched</div>{rtv.dispatched_at ? fmtDateTime(rtv.dispatched_at) : "—"}</div>
          <div><div className="text-xs text-muted-foreground">Closed</div>{rtv.closed_at ? fmtDateTime(rtv.closed_at) : "—"}</div>
          <div className="md:col-span-4"><div className="text-xs text-muted-foreground">Remarks</div><div className="whitespace-pre-wrap">{rtv.remarks}</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Returned lines ({returned} units)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>#</TableHead><TableHead>Item</TableHead><TableHead>Source</TableHead>
              <TableHead className="text-right">Returned</TableHead><TableHead className="text-right">Removed from defective stock</TableHead><TableHead>Remarks</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rtv.items.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="text-xs">{i.line_no}</TableCell>
                  <TableCell className="text-sm">{i.description} <KindPill kind={i.item_kind} /></TableCell>
                  <TableCell className="text-xs capitalize">{i.source.replace("_", " ")}</TableCell>
                  <TableCell className="text-right tabular-nums">{Number(i.quantity)}</TableCell>
                  <TableCell className="text-right tabular-nums">{rtv.status === "Draft" || rtv.status === "Cancelled" ? "—" : Number(i.stock_deducted)}</TableCell>
                  <TableCell className="text-xs">{i.remarks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Replacement</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Only <span className="font-medium">defective</span> returns are replaced here, line by line. A replacement is received as its own GRN marked as the replacement
            (it does not count toward the PO&apos;s outstanding quantity). Rejected quantities stay outstanding on the PO and are received through a normal GRN.
            Stock is posted afterwards from that GRN&apos;s normal receiving.
          </p>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item</TableHead><TableHead className="text-right">Returned</TableHead><TableHead className="text-right">Replaced</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              {rtv.status === "Dispatched" && can && <><TableHead className="w-28 text-right">Receive now</TableHead><TableHead>Remarks</TableHead></>}
            </TableRow></TableHeader>
            <TableBody>
              {rtv.items.filter((i) => i.replaceable).map((i) => {
                const left = Number(i.to_replace ?? 0);
                const bad = Number(repQty[i.id]) > left;
                return (
                  <TableRow key={i.id}>
                    <TableCell className="text-sm">{i.description} <KindPill kind={i.item_kind} /></TableCell>
                    <TableCell className="text-right tabular-nums">{Number(i.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(i.replaced ?? 0)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{left}</TableCell>
                    {rtv.status === "Dispatched" && can && <>
                      <TableCell><Input type="number" min={0} max={left} step="any" disabled={left <= 0} className={`h-8 text-right ${bad ? "border-red-500" : ""}`}
                        value={repQty[i.id] ?? ""} onChange={(e) => setRepQty((m) => ({ ...m, [i.id]: e.target.value }))} /></TableCell>
                      <TableCell><Input className="h-8" disabled={left <= 0} placeholder="Optional" value={repRemarks[i.id] ?? ""} onChange={(e) => setRepRemarks((m) => ({ ...m, [i.id]: e.target.value }))} /></TableCell>
                    </>}
                  </TableRow>
                );
              })}
              {rtv.items.every((i) => !i.replaceable) && <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground">No defective lines on this return - nothing to replace.</TableCell></TableRow>}
            </TableBody>
          </Table>
          {rtv.status === "Dispatched" && can && rtv.items.some((i) => i.replaceable) && (
            <div className="flex flex-wrap items-center gap-2">
              <Input className="w-64" placeholder="Vendor replacement reference (optional)" value={repRef} onChange={(e) => setRepRef(e.target.value)} />
              <Button variant="outline" disabled={act.isPending || !Object.values(repQty).some((q) => Number(q) > 0)}
                onClick={async () => {
                  await run("receive-replacement", {
                    replacement_reference: repRef || undefined,
                    items: rtv.items.filter((i) => Number(repQty[i.id]) > 0).map((i) => ({ rtv_item_id: i.id, accepted_quantity: Number(repQty[i.id]), remarks: repRemarks[i.id] || undefined })),
                  }, "Replacement GRN created");
                  setRepQty({}); setRepRemarks({});
                }}>Receive replacement (creates GRN)</Button>
            </div>
          )}
          {rtv.replacement_grns.length > 0 && (
            <div className="space-y-0.5">
              <div className="text-xs text-muted-foreground">Replacement receipts</div>
              {rtv.replacement_grns.map((g) => (
                <div key={g.id}><Link to="/grns/$grnId" params={{ grnId: g.id }} className="text-primary hover:underline">{g.grn_number}</Link> · {Number(g.quantity)} received
                  {rtv.replacement_reference ? <span className="text-muted-foreground"> · vendor ref {rtv.replacement_reference}</span> : null}</div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {(rtv.status === "Draft" || rtv.status === "Dispatched") && (
        can ? (
          <Card>
            <CardHeader><CardTitle className="text-base">Actions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {rtv.status === "Draft" && (
                <Alert><AlertTitle>Draft</AlertTitle><AlertDescription>Dispatching removes the returned defective quantity from defective stock (only what was posted there) and cannot be undone. Cancel releases the quantity.</AlertDescription></Alert>
              )}
              <Textarea rows={2} placeholder={rtv.status === "Draft" ? "Remarks (required to cancel)" : "Closing remarks (required)"} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              {rtv.status === "Draft" && <Input placeholder="Vendor reference / RMA (optional)" value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} />}
              <div className="flex flex-wrap gap-2">
                {rtv.status === "Draft" && <>
                  <Button disabled={act.isPending} onClick={() => run("dispatch", { remarks, vendor_reference: vendorRef || undefined }, "Return dispatched")}>
                    {act.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Dispatch to vendor</Button>
                  <Button variant="outline" disabled={act.isPending || !remarks.trim()} onClick={() => run("cancel", { remarks }, "Return cancelled")}>Cancel return</Button>
                </>}
                {rtv.status === "Dispatched" && <Button disabled={act.isPending || !remarks.trim()} onClick={() => run("close", { remarks }, "Return closed")}>Close return</Button>}
              </div>
            </CardContent>
          </Card>
        ) : <p className="text-xs text-muted-foreground">You can view this return but do not have permission to process it.</p>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">History</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          {rtv.history.map((h, i) => (
            <div key={i} className="flex flex-wrap gap-2 border-b py-1 last:border-0">
              <span className="w-40 text-xs text-muted-foreground">{fmtDateTime(h.at)}</span>
              <span className="w-36 font-medium capitalize">{h.action.replace("_", " ")}</span>
              <span className="text-xs">{h.actor ?? "—"}</span>
              <span className="text-xs text-muted-foreground">{h.remarks}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
